import _ from 'underscore';
import m from 'mithril';
import EventEmitter from 'wolfy87-eventemitter';

import Xml from "./xml";
import {XmlTagProvider} from "./xmlTagProvider";
import questionaryTagHandlers from "./tags/questionary";
import {Form} from "./form/form";
import FormGroup from "./form/formGroup";
import Api from "../api";
import EventReporter from "../eventReporter";
import Messages from "./messages";
import {Options} from "./options";
import Variable from "./variable";
import QuestionaryRandomizer from "./questionaryRandomizer";
import {ReactiveProperty} from "./reactive";
import {Filter, SubmitForm, FilterAction} from "./tags/expression";
import reactive = require('./reactive');
import SkipForm = FilterAction.SkipForm;
import GotoForm = FilterAction.GotoForm;
import GotoEnd = FilterAction.GotoEnd;
import HardScreenOut = FilterAction.HardScreenOut;
import QualityCheckOut = FilterAction.QualityCheckOut;
import {QuotaList} from "./tags/quota";
import utils = require('./utils');
import {Dictionary} from "./dictionary";
import {key as LocaleKeyType} from "flatpickr/dist/types/locale";

export interface QuestionaryVariable<T> extends ReactiveProperty<T> {
	computed?: boolean  //todo
}

type Status = "FINISHED"|"HARDSCREENOUT"|"QUOTA_FULL"|"QCOUT";

export interface QuestionaryState{

	variables: { [key: string] : QuestionaryVariable<any> },
	status: Status|null,

	random: {
		formGroup: number[][],
		formNumbers: any,
		form: any,
		saved: boolean
	},

	history: string[],
    finishedForms: string[],
    filterGoToJumps: [string, string][],

	quotas: any,
	attributes: { [key: string] : any };
	devices: { [key: string] : any }[];
}

//main questionary model
export class Questionary {

    static readonly LANGUAGE_ATTRIBUTE_NAME = 'respondent.lang';
    static readonly LANGUAGE_VARIABLE_NAME = 'lang';

	api: Api;
	messages: Messages;
	eventReporter?: EventReporter; // after messages, this param in constructor is optional
	randomizer?: QuestionaryRandomizer; // is initialized after load data

	quotas: QuotaList|null = null;
	variables = {};
	formsById: { [id:string]: Form } = {};
	options?: Options; // are initialized after load definitions

	progress: {current:number, all:number}|null = null;

	isInitialized = false;

    test: boolean = false;

	state: QuestionaryState = {
		variables: {},
		status: null,

		random: {
			formGroup: [],
			form: {},
			formNumbers: {},
			saved: false
		},

		history: [],
        finishedForms: [],
        filterGoToJumps: [],

		quotas: null,
		attributes: {},

        devices: [],
	};
	private ipAddress: string|null = null;

    dictionary?: Dictionary;

	customization: { formPreSubmitCallback: _.Dictionary<() => boolean>} = {
		formPreSubmitCallback: {}
	};


	private _currentForm: Form|null = null;
	private _rootFormGroup?: FormGroup; // is set after load definitions
	private firstForm: string|null = null; // is set from outsite, just form id (string)
	private eventEmitter: EventEmitter;
	public savingStatus: ''|'saving'|'saved'|'saved_withStatus'|'problems'|'failed' = '';
    private savingTimeout?: number;

	private cache_formList?: Form[];

	constructor(api: Api, messages?: Messages, eventReporter?: EventReporter) {

		this.api = api;
		this.messages = messages || new Messages();
		this.eventReporter = eventReporter;
		this.eventEmitter = new EventEmitter();
		this.eventEmitter.defineEvents(Questionary.Events.allEvents());
	}

	public get rootFormGroup(): FormGroup|undefined {
	    return this._rootFormGroup;
    }

    public guessLocale(): LocaleKeyType {
        // lang of current translation  ??  questionary lang
        return <LocaleKeyType>(this.messages.getLocale() || $('html').attr('lang'));
    }

	// Load async definitions and then data of questionary
	init (presetVariables?: {[variable:string]: string|number}) {
        this.createVariable(Questionary.LANGUAGE_VARIABLE_NAME, 'string', false, undefined);

		return this.loadDefinition().then(() => {
            this.trigger(Questionary.Events.QUESTIONARY_DEFINITION_LOADED);
            return this.loadData().then(() => {
                this.trigger(Questionary.Events.QUESTIONARY_DATA_LOADED);
                // Resolve IP address for service data
                this.api.getIpAddress().then(data => this.ipAddress = <string>data, () => this.ipAddress = null);

                // Set preset variables. This can change history and walking tree
                Object.entries(presetVariables || {}).forEach( ([name, value]) => {
                    const variable = this.variableByName(name);
                    if (variable) { // TODO přepisovat či nepřepisovat existující proměnné v dotazníku? Asi spíš ne
                        variable(value);
                    } else {
                        this.createVariable(name, 'string', false, value);
                    }
                });

                return this.loadDictionary().then(() => {
                    const lang = this.variableByName(Questionary.LANGUAGE_VARIABLE_NAME)();
                    if (lang) {
                        this.dictionary!.locale = ""+lang;
                        this.saveLang();
                    }
                    this.trigger(Questionary.Events.QUESTIONARY_DICTIONARY_LOADED);

                    Object.values(this.formsById).forEach( f => f.init() );
                    this.randomizeFormGroups();

                    this.updateProgress();
                    const currentFormId = _.last(this.state.history);

                    if (currentFormId) {
                        this.currentForm = this.formsById[currentFormId];
                    } else if (currentFormId === null) {
                        this.currentForm = null;
                        this.trigger(Questionary.Events.QUESTIONARY_FINISHED);
                    } else {
                        // Jump to first form
                        this.currentForm = this.getFirstForm();
                    }

                    this.initForm();

                    // Save survey if there is randomization or questionary already finished (with status) or preset variables
                    if (!this.state.random.saved || this.state.status || Object.entries(presetVariables || {}).length) {
                        this.save(); // save service data and also initial answers, to capture random synthetic variables
                    }

                    this.isInitialized = true;
                    this.trigger(Questionary.Events.QUESTIONARY_INITIALIZED);
                });
            });
		});
	}

	trigger(event: string, ...args: any[]): EventEmitter {
		if( !_.contains(Questionary.Events.allEvents(), event) ) {
			throw new Error(`Event '${event}' is not registered`);
		}
        if (process.env.NODE_ENV === 'development') {
            console.log('EVENT: '+event);
        }
		return this.eventEmitter.trigger(event, [event, ...args]);
	}
	on(event: RegExp|string, listener: Function): EventEmitter {
		return this.eventEmitter.on(<any>event, listener);
	}
	off(event: RegExp|string, listener: Function): EventEmitter {
		return this.eventEmitter.off(<any>event, listener);
	}

	get currentForm() {
		return this._currentForm;
	}
	set currentForm(f: Form|null) {
		this._currentForm = f;
		if( f ) {
			f.onShow();
		}
	}

	getFirstForm() {
		const firstFormId = this.firstForm ? this.firstForm: this._rootFormGroup!.firstId!; // this should be call after loadDefinitions
		const first = this.randomizer!.translateMove(null, firstFormId); //
		return first ? this.formsById[first] : null;
	}

	createVariable(name: string, type: string, doNotRedraw: boolean, initialValue: any) {
		const v = reactive.state(initialValue, type, !doNotRedraw);
		this.state.variables[name] = v;
		return v;
	}

	/**
	 * Get variable (Reactive.state) by name.
	 * @param variableName
	 * @returns {(value?:any)=>any}
	 */
	variableByName(variableName: string): QuestionaryVariable<any> {
		return this.state.variables[variableName];
	}

	loadDefinition  () {
		return this.api.getDefinition()
			.then((xmlString) => {
			    if (typeof xmlString === "string") {
                    const xml = new Xml(new XmlTagProvider(questionaryTagHandlers), {questionary: this});
                    const result = xml.process(xmlString);
                    this._rootFormGroup = result.rootFormGroup;
                    this.quotas = result.quotas;
                    this.options = result.options;
                } else {
			        throw new Error(`Expecting XML string with definitions, got ${typeof xmlString}.`);
                }
			});
	}

    loadDictionary  () {
        return this.api.getDictionary()
            .then((jsonString) => {

                let jsonObject;
                try {
                    jsonObject = jsonString ? JSON.parse(<string>jsonString) : {};
                } catch (e) {
                    return;
                }

                if (_.has(jsonObject, 'translations')) {
                    // @ts-ignore
                    const hashLang : string = this.getHashParams()[Questionary.LANGUAGE_ATTRIBUTE_NAME];
                    this.dictionary = new Dictionary(jsonObject.translations, hashLang);
                    if (hashLang) {
                        this.saveLang();
                    }
                }
            });
    }

	loadData  () {
		const answers = this.api.getAnswers().then((data) => {
			_.each(data, (value, name: string) => {
				const variable = this.variableByName(name);
				if (!variable) {
					// console.log("Saved variable, which is not defined:", name);
				} else {
					variable(value);
				}
			});
		});

		const serviceData = this.api.getServiceData().then(data => {
			if (data?.randomization) {
				this.state.random = data.randomization;
				this.state.history = data.history || [];
				this.state.finishedForms = data.finishedForms || [];
				this.state.filterGoToJumps = data.filterGoToJumps || [];
				this.state.devices = data.devices || [];
				this.state.random.saved = true;
			}
			if (data?.quotas) {
                // load first quotas from service data #raceConditionWarning
                this.state.quotas = data.quotas;
            }

		});

		const attributes = this.api.getAttributes().then( (attributes:any) => {
            attributes['respondent.screenSize'] = utils.screenSizeTextual();
			this.state.attributes = attributes;
		});

		const quotas = this.api.getQuotas().then(freshQuotasFromApi => {
		    if (!this.state.quotas) {
		        // do not rewrite first quotas, loaded from service data #raceConditionWarning
                this.state.quotas = freshQuotasFromApi;
            }
		});

		return Promise.all([answers, serviceData, attributes, quotas]);
	}

    /**
     * Save answers, service data and status of questionary if it is finished
     */
	public save(tries: number = 3, delay: number = 0) {
	    if (delay) {
	        if (this.savingTimeout) {
	            window.clearTimeout(this.savingTimeout)
            }
	        this.savingTimeout = window.setTimeout(() => this.save(tries, 0), delay);
	        return;
        }

	    if (tries < 1) {
            this.savingStatus = 'failed';
            this.eventReporter?.report('QUESTIONARY_SAVING_FAILED', 'Saving failed after 3 attempts.');
            m.redraw();
	        return;
        }
        this.savingStatus = 'saving';
		this.trigger(Questionary.Events.ANSWERS_SAVING);

        const pendingEvents = this.eventReporter?.pendingEvents ?? [];
        if (this.eventReporter) {
            this.eventReporter.pendingEvents = [];
        }

		return this.api.saveData({
            answerData: this.answerData,
            serviceData: this.serviceData,
            events: pendingEvents,
        }).then(
		    () => {
                this.trigger(Questionary.Events.ANSWERS_SAVED);
                this.savingStatus = 'saved';
                if (this.currentForm == null) {
                    this.eventReporter?.sendPending();
                    this.saveFinishedStatus(3);
                }
                m.redraw();
            },
            () => { // On reject try again after 3 seconds
                this.savingStatus = 'problems';
                pendingEvents.forEach(e => this.eventReporter?.pendingEvents?.push(e));
                m.redraw();
		        setTimeout(() => this.save(tries-1, 0), 3000);
            }
		);
	}

    /**
     *  Save status of questionary - mark questionary finished
     */
	private saveFinishedStatus(tries: number = 3) {
        if (tries < 1) {
            this.savingStatus = 'failed';
            m.redraw();
            return;
        }
        this.savingStatus = 'saving';

        this.api.saveStatus({
            status: this.state.status || 'FINISHED'
        }).then(
            () => {
                this.savingStatus = 'saved_withStatus';
                this.trigger(Questionary.Events.QUESTIONARY_SAVED_WITH_STATUS);
                m.redraw();
            },
            () => { // On reject try again after 3 seconds
                this.savingStatus = 'problems';
                m.redraw();
                setTimeout(() => this.saveFinishedStatus(tries-1), 3000);
            }
        );
    }

	private get serviceData() {

		return {
			randomization: this.state.random,
			history: this.state.history,
            finishedForms: this.state.finishedForms,
            filterGoToJumps: this.state.filterGoToJumps,
            devices: [
                ...this.state.devices,
                {
                    userAgent: window.navigator.userAgent,
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height,
                    // @ts-ignore
                    screenOrientationType: window.screen.msOrientation || window.screen.orientation?.type || (window.screen.width > window.screen.height ? 'landscape-guess' : 'portrait-guess'),
                    touchInputOnly: Boolean(window.matchMedia("(pointer: coarse)").matches), // https://stackoverflow.com/a/52855084/11350567
                    viewportWidth: window.innerWidth,
                    prefersColorScheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
                    // @ts-ignore
                    downLinkSpeed: window.navigator.connection?.downlink || null,
                    ipAddress: this.ipAddress,
                    time: utils.timestamp(),
                },
            ],
            quotas: this.state.quotas,
		};
	}

	private get answerData() {
	    return this.state.variables;
    }

	next () {
        this.trigger(Questionary.Events.FORM_BEFORE_CHANGED, this.currentForm);

		// Check customization preSubmit handler
		const callback = this.currentForm ? this.customization.formPreSubmitCallback[this.currentForm.id] : null;
		if( callback && !callback() ) {
			// PreSubmit callback registered and returned false -> cancel submission
			return false;
		}

        let res = false;
		if (this.currentForm && this.currentForm.validate()) {
            this.state.finishedForms.push(this.currentForm.id);
            this.currentForm.onNext();
			this.save();
			this.skip();
            this.scrollTop();
			res = true;
		}

		return res;
	}

	prev () {
		const lastFormId = this.state.history.pop();

        this.state.finishedForms = this.state.finishedForms.filter(id => id !== lastFormId);

		this.currentForm!.backupAndClear(); // current form should be set if prev is called
		this.save();
		this.goTo(this.state.history.length ? _.last(this.state.history) : '01'); // prevent jump before first form (before history)
		this.updateProgress();
	}


	updateProgress () {
		const all = _.keys(this.formsById).length;
		this.progress = {
			current: this.currentForm ? this.currentForm.getNumber() : all,
			all: all
		};
	}


	addToHistory (record?: string) {
		if (record && record !== _.last(this.state.history)) {
			this.state.history.push(record);
			this.updateProgress();
		}
	}


	checkQuota () {
        if (this.currentForm && this.quotas && this.quotas.isQuotaFull(this.state, this.currentForm)) {

            // Report full quota
            const fullQuota = this.quotas.getFullQuota();
            if (fullQuota) {
                const fullQuotaDetails = {
                    form: this.currentForm.id,
                    quotaVariable: fullQuota.variable.name,
                    quotaCellType: fullQuota.cell.type,
                    quotaCell: fullQuota.cell.text,
                };
                this.trigger(Questionary.Events.QUOTA_FULL, fullQuotaDetails);
                this.eventReporter?.report(Questionary.Events.QUOTA_FULL, fullQuotaDetails);
            }

            if (!this.test) {
			    this.end('QUOTA_FULL');
            } else {
			    console.log('QUOTA_FULL, but this is just test run, so stay cool <3');
            }
		}
	}

	/**
	 * Process filter
	 * @param filter
	 */
	processFilter(filter: Filter) {
		this.trigger(Questionary.Events.NOTIFY_FILTER_ACTIVE, filter.describe());

		const action = filter.action;
		if (action instanceof SubmitForm) {
			this.next();
		} else if (action instanceof SkipForm) {
			this.skip();
		} else if (action instanceof GotoForm) {
            this.state.filterGoToJumps.push([this.currentForm!.id, action.target]);
			this.goTo((<GotoForm>action).target);
		} else if (action instanceof GotoEnd) {
			this.end(null);
		} else if (action instanceof HardScreenOut) {
			this.end('HARDSCREENOUT');
		} else if (action instanceof QualityCheckOut) {
			this.end('QCOUT');
		}
	}

	end (status: Status|null) {
	    if (this.currentForm) {
		    this.currentForm.stopTiming();
        }
		this.currentForm = null;
		this.state.status = status;

        this.initForm();
        m.redraw();
	}

	initForm (ignorePreFilters = false) {

		this.checkQuota();

		if (this.currentForm) {
			if( ignorePreFilters ) {
				this.currentForm.onNavigatedTo();
			} else {
				// Process preFilter
				const preFilter = this.currentForm.getActivePreFilter();
				if( preFilter ) {
					this.processFilter(preFilter);
				} else {
					this.currentForm.onNavigatedTo();
				}
			}
		}

		// current form should be changed by filter action
		this.addToHistory(this.currentForm?.id);

		this.trigger(Questionary.Events.FORM_CHANGED, this.currentForm);
	}


	skip () {
		const postFilter = this.currentForm ? this.currentForm.getActivePostFilter() : null;
		if( postFilter ) {
			this.processFilter(postFilter);
		} else {
			this.checkQuota();
			if( this.currentForm && this.currentForm.nextFormId) {
				this.goTo(this.currentForm.nextFormId);
			}
		}
	}

	goTo (toFormId: string, noJumpAndPreFilter = false) {

		if(!noJumpAndPreFilter){
			toFormId = this.randomizer!.translateMove(this.currentForm, toFormId)!; // randomizer is initialized after load data
		}

		if (this.currentForm) {
			this.currentForm.stopTiming();
		}
		this.currentForm = this.formsById[toFormId];
		this.initForm(noJumpAndPreFilter);
		m.redraw();
	}

	getFileUrl (filename: string) {
		return this.api.getFileUrl(filename);
	}

	getVariableLabel (variableName: string) {
		const pVariable = this.variableByName(variableName);
		const variable: Variable = _.values(this.formsById).reduce((acc, form) => form.variables[variableName] || acc, undefined);
		if (_.isUndefined(pVariable) || _.isUndefined(variable)) {
			// TODO really could happen in valid questionary? if not, log questionary inconsistency
			return "";
		}
		const value = pVariable();
		if( variable.isText() ) {
            return value;
        }

        const inputText = variable.getInputText(value);
        if (inputText) {
            return this.variableByName(inputText)();
        }

        const label = variable.getLabel(value);
        if (label !== '') {
            return label;
        }

        return value;
	}

    renderText (text: string) {
        let preparedText = text
            // replace respondentLink
            .replace(/<respondentLink +attribute="([^"]+)" *\/>/g, (match, attribute) => {
                // TODO add method readRespondentAttribute to state
                function addPrefixIfNotPresent(prefix: string, key: string) {
                    return key.indexOf('.') === -1 ? prefix + '.' + key : key;
                }

                return this.state.attributes[addPrefixIfNotPresent('respondent', attribute)];
            })
            // replace answerLink
            .replace(/<answerLink +variable="([^"]+)" *\/>/g, (match, variable) => this.getVariableLabel(variable) ?? '')

            // replace answerLink
            .replace(/<variableLink +variable="([^"]+)" *\/>/g, (match, variable) => this.variableByName(variable)?.() ?? '');

        if (text !== preparedText) {
            preparedText = this.renderText(preparedText);
        }

        return this.dictionary ? this.dictionary.translate(preparedText) : preparedText;
    }

	registerFormPreSubmitCallback(form: Form, cb: () => boolean) {
		this.customization.formPreSubmitCallback[form.id] = cb;
	}

	getFormList() : Form[] {
		this.cache_formList = this.cache_formList ?? this._rootFormGroup?.getFormsRecursive();
		return this.cache_formList ?? [];
	}

	setPresetFirstForm(firstForm: string) {
		this.firstForm = firstForm;
	}

    saveLang(): void {
	    const lang = this.dictionary!.locale;
        this.state.attributes[Questionary.LANGUAGE_ATTRIBUTE_NAME] = lang;
        this.createVariable(Questionary.LANGUAGE_VARIABLE_NAME, 'string', true, lang);
        this.messages.setLocale(lang!);
        this.options!.onLangChange();
        if (this.currentForm) {
            this.goTo(this.currentForm.id, false);
        }
        this.trigger(Questionary.Events.QUESTIONARY_LANGUAGE_CHANGED, lang);
    }

    scrollTop() {
        requestAnimationFrame(() => {
            if (document.scrollingElement) {
                document.scrollingElement.scrollTop = 0;
            } else {
                window.scrollTo(0,0);
            }
        })
    };

	private randomizeFormGroups(){
		this.randomizer = new QuestionaryRandomizer(
			this._rootFormGroup!, // is already defined, set after load definitions
			this.state.random.formGroup,
			this.state.random.formNumbers);

		let { groupPermutations, formNumbers } = this.randomizer.process();
		this.state.random.formGroup = groupPermutations;
		this.state.random.formNumbers = formNumbers;
	};

	private getHashParams() {
        // http://www.timetler.com/2013/11/14/location-search-split-one-liner/
	    return _.object(_.compact(_.map(window.location.hash.slice(1).split('&'), function(i) { return i ?  i.split('=') : undefined; })));
    }
}

export module Questionary {
	export class Events {
		public static SUBMIT_FORM = 'submitForm';
		public static EVENTS_COMPUTED = 'eventsComputed';
		public static QUESTIONARY_FINISHED = 'questionaryFinished'; // po úspěšném ukončení dotazníku (včetně feedbacku)
		public static QUESTIONARY_SAVED_WITH_STATUS = 'questionarySavedWithStatus'; // po dokončení API client POST ../status, data jsou uložena a uzamčena
		public static QUESTIONARY_INITIALIZED = 'questionaryInitialized';
		public static QUESTIONARY_DEFINITION_LOADED = 'questionaryDefinitionLoaded';
		public static QUESTIONARY_DATA_LOADED = 'questionaryDataLoaded';
		public static QUESTIONARY_DICTIONARY_LOADED = 'questionaryDictionaryLoaded';
		public static QUESTIONARY_LANGUAGE_CHANGED = 'questionaryLanguageChanged';
        public static SURVEY_LOADING = 'surveyLoading';
		public static ANSWERS_SAVING = 'answersSaving';
		public static ANSWERS_SAVED = 'answersSaved'; // po úspěšném uložení odpovědí přes API client: PUT ../answers, nyní je možné si data stáhnout přes API data ze Survey Server.
        public static FORM_BEFORE_CHANGED = 'formBeforeChanged'; // call when on form is leaved just before post filters
		public static FORM_CHANGED = 'formChanged'; // call after form changed just before show it to respondent
		public static NOTIFY_EVENT_ACTIVE = 'notifyEventActive';
		public static NOTIFY_FILTER_ACTIVE = 'notifyFilterActive';
		public static NOTIFY_SYNTHETIC_VARIABLE_VALUE_UPDATE = 'notifySyntheticVariableValueUpdate';
		public static NOTIFY_QUOTA_ACTIVE = 'notifyQuotaActive';
		public static QUOTA_FULL = 'quotaFull';
        public static SCREENOUT = 'screenout';

		static allEvents(): string[] {
		    // @ts-ignore
		    return Object.keys(Questionary.Events).map(k => Questionary.Events[k]);
            //return Object.values(Questionary.Events);
		}
	}
}